import React, { useEffect, useState } from 'react';
import {
    Avatar,
    Box,
    Divider,
    Flex,
    Grid,
    SimpleGrid,
    Text,
} from "@chakra-ui/react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { IoIosPeople } from "react-icons/io";
import { MdPages, MdRefresh } from "react-icons/md";
import { FaSchool } from "react-icons/fa";
import { RiParentFill } from "react-icons/ri";
import { GiDoctorFace } from "react-icons/gi";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { useNavigate } from "react-router-dom";
import { StudentScore } from "./StudentScore";
import ModernLoader from "../../components/ModernLoader";

// ─── Design Tokens (DESIGN.md) ────────────────────────────────────────────────
const BRAND_BLUE  = "#1b73e8";
const CANVAS_BG   = "#f4f1ec";
const CARD_BG     = "#ffffff";
const CARD_SHADOW = "0 1px 4px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04)";
const CARD_HOVER  = "0 6px 20px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.04)";
const TEXT_DARK   = "#1a202c";
const TEXT_MID    = "#4a5568";
const TEXT_MUTED  = "#718096";
const BORDER      = "#e8e4de";

// Chart palette — harmonious, distinguishable
const CHART_COLORS = [
    '#1b73e8', // brand blue
    '#059669', // emerald
    '#d97706', // amber
    '#7c3aed', // violet
    '#db2777', // pink
    '#0891b2', // cyan
    '#65a30d', // lime
    '#ea580c', // orange
    '#4f46e5', // indigo
];

const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text
            x={x} y={y}
            fill="white"
            textAnchor="middle"
            dominantBaseline="central"
            style={{ fontSize: '12px', fontWeight: 700, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}
        >
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
};

// ─── Reusable Chart Legend ────────────────────────────────────────────────────
function ChartLegend({ data }) {
    return (
        <Flex flexWrap="wrap" justify="center" gap={3} mt={4} px={2}>
            {data.filter(d => d.value > 0).map((item, index) => (
                <Flex key={item.name} alignItems="center" gap="6px">
                    <Box
                        w="8px" h="8px"
                        borderRadius="2px"
                        bg={CHART_COLORS[index % CHART_COLORS.length]}
                        flexShrink={0}
                    />
                    <Text fontSize="12px" fontWeight={500} color={TEXT_MID}>
                        {item.name}: <strong>{item.value}</strong>
                    </Text>
                </Flex>
            ))}
        </Flex>
    );
}

// ─── Reusable Card Shell ──────────────────────────────────────────────────────
function DashCard({ children, title, noPad = false, ...rest }) {
    return (
        <Box
            bg={CARD_BG}
            borderRadius="16px"
            boxShadow={CARD_SHADOW}
            border={`1px solid ${BORDER}`}
            overflow="hidden"
            {...rest}
        >
            {title && (
                <Box px={5} pt={5} pb={3} borderBottom={`1px solid ${BORDER}`}>
                    <Text fontSize="14px" fontWeight={700} color={TEXT_DARK} letterSpacing="0.01em">
                        {title}
                    </Text>
                </Box>
            )}
            <Box p={noPad ? 0 : 5}>
                {children}
            </Box>
        </Box>
    );
}

// ─── Pie Chart Card ───────────────────────────────────────────────────────────
function PieCard({ title, data }) {
    const filtered = data.filter(d => d.value > 0);
    return (
        <DashCard title={title}>
            {filtered.length === 0 ? (
                <Flex h="200px" align="center" justify="center">
                    <Text color={TEXT_MUTED} fontSize="sm">No data available</Text>
                </Flex>
            ) : (
                <>
                    <Box w="100%" h="240px">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={filtered}
                                    labelLine={false}
                                    label={renderCustomizedLabel}
                                    outerRadius="80%"
                                    dataKey="value"
                                    strokeWidth={2}
                                    stroke="#fff"
                                >
                                    {filtered.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{
                                        borderRadius: '10px',
                                        border: `1px solid ${BORDER}`,
                                        boxShadow: CARD_SHADOW,
                                        fontSize: '13px',
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>
                    <ChartLegend data={filtered} />
                </>
            )}
        </DashCard>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function HomePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [dashboardData, setDashBoardData] = useState();
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [pieChartDataByGender, setPieChartDataByGender] = useState([]);
    const [pieChartDataTeacherByGender, setPieChartDataTeacherByGender] = useState([]);
    const [pieChartDataTherapistByGender, setPieChartDataTherapistByGender] = useState([]);
    const [countByDisabilityType, setCountByDisabilityType] = useState([]);
    const [countByDisabilityId, setCountByDisabilityId] = useState([]);

    useEffect(() => { onGetDashBoardData(); }, []);

    const onGetDashBoardData = () => {
        axios.post('/dashboard', {}).then((res) => {
            setPieChartDataByGender([
                { name: "Male",   value: res.data.studentData.totalBoys ?? 0 },
                { name: "Female", value: res.data.studentData.totalGirls ?? 0 },
                { name: "Others", value: res.data.studentData.totalOthersGender ?? 0 },
            ]);
            setPieChartDataTeacherByGender([
                { name: "Male",   value: res.data.teacherData.totalMaleTeacher ?? 0 },
                { name: "Female", value: res.data.teacherData.totalFemaleTeacher ?? 0 },
                { name: "Others", value: res.data.teacherData.totalOthersTeacher ?? 0 },
            ]);
            setPieChartDataTherapistByGender([
                { name: "Male",   value: res.data.teacherData.totalMaleTherapist ?? 0 },
                { name: "Female", value: res.data.teacherData.totalFemaleTherapist ?? 0 },
                { name: "Others", value: res.data.teacherData.totalOthersTherapist ?? 0 },
            ]);

            if (res.data.countByDisabilityType) {
                setCountByDisabilityType([
                    { name: "Speech Related",        value: res.data.countByDisabilityType['Speech Related'] ?? 0 },
                    { name: "Visual Disability",     value: res.data.countByDisabilityType['Visual Disability'] ?? 0 },
                    { name: "Disability",            value: res.data.countByDisabilityType['Disability'] ?? 0 },
                    { name: "Autism",                value: res.data.countByDisabilityType['Autism'] ?? 0 },
                    { name: "Physicy Disability",    value: res.data.countByDisabilityType['Physicy Disability'] ?? 0 },
                    { name: "Multiple Disabilities", value: res.data.countByDisabilityType['Multiple Disabilities'] ?? 0 },
                    { name: "Intellectual Disability", value: res.data.countByDisabilityType['Intellectual Disability'] ?? 0 },
                    { name: "Hearing Disability",    value: res.data.countByDisabilityType['Hearing Disability'] ?? 0 },
                    { name: "Deafblindness",         value: res.data.countByDisabilityType['Deafblindness'] ?? 0 },
                ]);
            }
            if (res.data.countByDisabilityId) {
                setCountByDisabilityId([
                    { name: "Profound (Red)",  value: res.data.countByDisabilityId['Profound Disability (Red Color ID)'] ?? 0 },
                    { name: "Moderate (Yellow)", value: res.data.countByDisabilityId['Moderate Disability (Yellow Color ID)'] ?? 0 },
                    { name: "Mild (White)",    value: res.data.countByDisabilityId['Mild Disability (White Color ID)'] ?? 0 },
                    { name: "Severe (Blue)",   value: res.data.countByDisabilityId['Severe Disability (Blue Color ID)'] ?? 0 },
                ]);
            }

            setDashBoardData(res.data);
            setLoading(false);
            setRefreshing(false);
        });
    };

    const onRefresh = () => {
        setRefreshing(true);
        axios.post('/dashboard/refresh').then(() => onGetDashBoardData());
    };

    const statCards = [
        { label: t('studentLocal:students'), value: dashboardData?.studentData?.totalStudents ?? 0, icon: IoIosPeople, color: BRAND_BLUE,  path: '/my_students' },
        { label: t('common:plans'),          value: dashboardData?.teacherData?.totalPlans ?? 0,    icon: MdPages,      color: '#059669',   path: null },
        { label: t('school:title'),          value: dashboardData?.studentData?.totalSchool ?? 0,   icon: FaSchool,     color: '#d97706',   path: '/school' },
        { label: t('users:PARENT'),          value: dashboardData?.teacherData?.totalParent ?? 0,   icon: RiParentFill, color: '#7c3aed',   path: '/user?role=PARENT' },
        { label: t('users:THERAPIST'),       value: dashboardData?.teacherData?.totalTherapist ?? 0, icon: GiDoctorFace, color: '#db2777',  path: '/user?role=THERAPIST' },
    ];

    if (loading) {
        return <ModernLoader h="60vh" message="Gathering dashboard metrics..." />;
    }

    return (
        <Box fontFamily="heading" pb={8}>

            {/* ── Page Header ── */}
            <Flex
                justifyContent="space-between"
                alignItems="flex-end"
                mb={7}
            >
                <Box>
                    <Text
                        fontSize={{ base: '22px', md: '26px' }}
                        fontWeight={800}
                        color={TEXT_DARK}
                        letterSpacing="-0.02em"
                        lineHeight={1}
                        mb="4px"
                    >
                        Dashboard
                    </Text>
                    <Text fontSize="13px" color={TEXT_MUTED}>
                        Overview of your IEP management metrics
                    </Text>
                </Box>

                {/* Refresh button */}
                <Box
                    as="button"
                    display="flex"
                    alignItems="center"
                    gap="6px"
                    px="14px"
                    py="8px"
                    borderRadius="10px"
                    bg={CARD_BG}
                    border={`1px solid ${BORDER}`}
                    boxShadow={CARD_SHADOW}
                    color={BRAND_BLUE}
                    fontSize="13px"
                    fontWeight={600}
                    cursor="pointer"
                    onClick={onRefresh}
                    _hover={{ bg: BRAND_BLUE, color: 'white', borderColor: BRAND_BLUE }}
                    transition="all 0.18s ease"
                    disabled={refreshing}
                    style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }}
                >
                    <MdRefresh
                        size={15}
                        style={{
                            animation: refreshing ? 'spin 0.8s linear infinite' : 'none',
                        }}
                    />
                    {refreshing ? 'Refreshing...' : 'Refresh Data'}
                </Box>
            </Flex>

            {/* ── KPI Stat Cards ── */}
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 5 }} spacing={4} mb={7}>
                {statCards.map(({ label, value, icon: Icon, color, path }) => (
                    <Box
                        key={label}
                        bg={CARD_BG}
                        borderRadius="16px"
                        p={5}
                        boxShadow={CARD_SHADOW}
                        border={`1px solid ${BORDER}`}
                        cursor={path ? 'pointer' : 'default'}
                        onClick={path ? () => navigate(path) : undefined}
                        _hover={path ? {
                            boxShadow: CARD_HOVER,
                            transform: 'translateY(-2px)',
                            borderColor: color,
                        } : {}}
                        transition="all 0.20s ease"
                        position="relative"
                        overflow="hidden"
                    >
                        {/* Subtle top accent bar */}
                        <Box
                            position="absolute"
                            top={0} left={0} right={0}
                            h="3px"
                            bg={color}
                            borderRadius="16px 16px 0 0"
                        />

                        {/* Icon chip */}
                        <Box
                            w="40px" h="40px"
                            borderRadius="10px"
                            bg={`${color}18`}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            mb={3}
                        >
                            <Icon size={20} color={color} />
                        </Box>

                        <Text
                            fontSize="12px"
                            fontWeight={600}
                            color={TEXT_MUTED}
                            textTransform="uppercase"
                            letterSpacing="0.07em"
                            mb={1}
                            noOfLines={1}
                        >
                            {label}
                        </Text>
                        <Text
                            fontSize={{ base: '28px', md: '32px' }}
                            fontWeight={800}
                            color={TEXT_DARK}
                            lineHeight={1}
                            letterSpacing="-0.03em"
                        >
                            {value.toLocaleString()}
                        </Text>
                    </Box>
                ))}
            </SimpleGrid>

            {/* ── Upcoming Reviews + Student Gender ── */}
            <Grid
                templateColumns={{ base: '1fr', lg: dashboardData.upcomingReviews ? '1fr 1fr' : '1fr' }}
                gap={5}
                mb={5}
            >
                {/* Upcoming Reviews */}
                {dashboardData.upcomingReviews && (
                    <DashCard title="Upcoming Reviews" noPad>
                        <Box px={5} pb={5}>
                            {dashboardData.upcomingReviews.slice(0, 6).map((s, index) => (
                                <Box key={s.studentId || index}>
                                    <Flex
                                        align="center"
                                        justify="space-between"
                                        py={3}
                                        px={2}
                                        borderRadius="10px"
                                        cursor="pointer"
                                        _hover={{ bg: '#f7fafc' }}
                                        transition="background 0.15s"
                                        onClick={() => navigate(`/students-profile/${s.studentId}`)}
                                    >
                                        <Flex align="center" gap={3}>
                                            <Avatar src={s.studentImage} name={s.studentName} size="sm" />
                                            <Box>
                                                <Text fontSize="14px" fontWeight={600} color={TEXT_DARK}>{s.studentName}</Text>
                                                <Text fontSize="12px" color={TEXT_MUTED}>{`IEP of ${s.year}`}</Text>
                                            </Box>
                                        </Flex>
                                        <Box
                                            px="10px"
                                            py="4px"
                                            borderRadius="20px"
                                            bg={index % 2 === 0 ? '#d1fae5' : '#fef3c7'}
                                            border={`1px solid ${index % 2 === 0 ? '#6ee7b7' : '#fcd34d'}`}
                                        >
                                            <Text
                                                fontSize="11px"
                                                fontWeight={700}
                                                color={index % 2 === 0 ? '#065f46' : '#92400e'}
                                                letterSpacing="0.02em"
                                            >
                                                {new Date(s.reviewTimestamp).toDateString()}
                                            </Text>
                                        </Box>
                                    </Flex>
                                    {index < Math.min(dashboardData.upcomingReviews.length - 1, 5) && (
                                        <Divider borderColor={BORDER} />
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </DashCard>
                )}

                {/* Students by Gender */}
                <PieCard
                    title="Students by Gender"
                    data={pieChartDataByGender}
                />
            </Grid>

            {/* ── Disability Charts ── */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mb={5}>
                <PieCard
                    title="Students by Disability Type"
                    data={countByDisabilityType}
                />
                <PieCard
                    title="Students by Disability ID"
                    data={countByDisabilityId}
                />
            </SimpleGrid>

            {/* ── Staff Gender Charts ── */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mb={5}>
                <PieCard
                    title="Teachers by Gender"
                    data={pieChartDataTeacherByGender}
                />
                <PieCard
                    title="Therapists by Gender"
                    data={pieChartDataTherapistByGender}
                />
            </SimpleGrid>

            {/* ── Student Review Scores Bar Chart ── */}
            <DashCard title="Student Review Scores by Year">
                <StudentScore />
            </DashCard>

            {/* Spin keyframe for refresh icon */}
            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>
        </Box>
    );
}
